import React, { useState } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { Mail, Lock, Save, Shield, Heart } from 'lucide-react'
import { Button } from './ui/button'
import { Input } from './ui/input'

interface RegistrationProps {
  onRegister: (email: string, password: string) => void
}

export default function Registration({ onRegister }: RegistrationProps) {
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [confirmPassword, setConfirmPassword] = useState('')
  const [errors, setErrors] = useState<string[]>([])
  const [isLoading, setIsLoading] = useState(false)

  const validateForm = () => {
    const newErrors: string[] = []

    if (!email) {
      newErrors.push('Email è richiesta')
    } else if (!/\S+@\S+\.\S+/.test(email)) {
      newErrors.push('Email non valida')
    }

    if (!password) {
      newErrors.push('Password è richiesta')
    } else if (password.length < 6) {
      newErrors.push('Password deve essere almeno 6 caratteri')
    }

    if (password !== confirmPassword) {
      newErrors.push('Le password non coincidono')
    }

    setErrors(newErrors)
    return newErrors.length === 0
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (validateForm()) {
      setIsLoading(true)
      // Simulate API call
      await new Promise(resolve => setTimeout(resolve, 1500))
      onRegister(email, password)
      setIsLoading(false)
    }
  }

  return (
    <div className="space-y-8">
      <motion.div 
        className="text-center"
        initial={{ y: -20, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ duration: 0.6 }}
      >
        <motion.div 
          className="inline-flex items-center justify-center w-20 h-20 bg-gradient-to-br from-blue-100 to-purple-100 rounded-full mb-6 shadow-xl"
          animate={{ 
            scale: [1, 1.05, 1],
            rotate: [0, 5, -5, 0]
          }}
          transition={{ 
            duration: 4, 
            repeat: Infinity,
            ease: "easeInOut"
          }}
        >
          <Shield className="w-10 h-10 text-blue-600" />
        </motion.div>
        
        <motion.h2 
          className="text-2xl font-bold bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent mb-3"
          initial={{ y: 20, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          transition={{ delay: 0.2 }}
        >
          Proteggi i Tuoi Ricordi ✨
        </motion.h2>
        
        <motion.p 
          className="text-gray-600 text-lg max-w-sm mx-auto leading-relaxed"
          initial={{ y: 20, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          transition={{ delay: 0.3 }}
        >
          Registrati per mantenere al sicuro il tuo viaggio emotivo
        </motion.p>
      </motion.div>

      <motion.form 
        onSubmit={handleSubmit} 
        className="space-y-6"
        initial={{ y: 30, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ delay: 0.4 }}
      >
        <motion.div 
          className="space-y-2"
          whileHover={{ scale: 1.02 }}
        >
          <label className="text-sm font-semibold text-gray-700 flex items-center space-x-2">
            <Mail className="w-4 h-4" />
            <span>Email</span>
          </label>
          <div className="relative">
            <motion.div
              className="absolute left-4 top-4 z-10"
              animate={{ 
                scale: email ? [1, 1.2, 1] : 1,
                color: email ? '#58786D' : '#9ca3af'
              }}
              transition={{ duration: 0.3 }}
            >
              <Mail className="w-5 h-5" />
            </motion.div>
            <Input
              type="email"
              placeholder="la-tua-email@esempio.com"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="pl-12 h-14 text-base border-2 border-gray-200 focus:border-purple-300 rounded-2xl shadow-lg bg-white/80 backdrop-blur-sm"
            />
          </div>
        </motion.div>

        <motion.div 
          className="space-y-2"
          whileHover={{ scale: 1.02 }}
        >
          <label className="text-sm font-semibold text-gray-700 flex items-center space-x-2">
            <Lock className="w-4 h-4" />
            <span>Password</span>
          </label>
          <div className="relative">
            <motion.div
              className="absolute left-4 top-4 z-10"
              animate={{ 
                scale: password ? [1, 1.2, 1] : 1,
                color: password ? '#58786D' : '#9ca3af'
              }}
              transition={{ duration: 0.3 }}
            >
              <Lock className="w-5 h-5" />
            </motion.div>
            <Input
              type="password"
              placeholder="Almeno 6 caratteri"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="pl-12 h-14 text-base border-2 border-gray-200 focus:border-purple-300 rounded-2xl shadow-lg bg-white/80 backdrop-blur-sm"
            />
          </div>
        </motion.div>

        <motion.div 
          className="space-y-2"
          whileHover={{ scale: 1.02 }}
        >
          <label className="text-sm font-semibold text-gray-700 flex items-center space-x-2">
            <Lock className="w-4 h-4" />
            <span>Conferma Password</span>
          </label>
          <div className="relative">
            <motion.div
              className="absolute left-4 top-4 z-10"
              animate={{ 
                scale: confirmPassword ? [1, 1.2, 1] : 1,
                color: confirmPassword && password === confirmPassword ? '#10b981' : '#9ca3af'
              }}
              transition={{ duration: 0.3 }}
            >
              <Lock className="w-5 h-5" />
            </motion.div>
            <Input
              type="password"
              placeholder="Ripeti la password"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              className="pl-12 h-14 text-base border-2 border-gray-200 focus:border-purple-300 rounded-2xl shadow-lg bg-white/80 backdrop-blur-sm"
            />
          </div>
        </motion.div>

        <AnimatePresence>
          {errors.length > 0 && (
            <motion.div 
              className="bg-red-50 border-2 border-red-200 rounded-2xl p-4 shadow-lg"
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: 'auto' }}
              exit={{ opacity: 0, height: 0 }}
            >
              <ul className="text-sm text-red-700 space-y-2">
                {errors.map((error, index) => (
                  <motion.li 
                    key={index}
                    initial={{ x: -10, opacity: 0 }}
                    animate={{ x: 0, opacity: 1 }}
                    transition={{ delay: index * 0.1 }}
                    className="flex items-center space-x-2"
                  >
                    <span className="w-1 h-1 bg-red-500 rounded-full" />
                    <span>{error}</span>
                  </motion.li>
                ))}
              </ul>
            </motion.div>
          )}
        </AnimatePresence>

        <motion.div whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.98 }}>
          <Button 
            type="submit" 
            disabled={isLoading}
            className="w-full bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 text-white border-0 h-14 text-lg font-semibold rounded-2xl shadow-xl"
            size="lg"
          >
            {isLoading ? (
              <motion.div
                animate={{ rotate: 360 }}
                transition={{ duration: 1, repeat: Infinity, ease: "linear" }}
                className="w-6 h-6 border-2 border-white border-t-transparent rounded-full"
              />
            ) : (
              <>
                <Save className="w-6 h-6 mr-3" />
                Unisciti a Gitaigo
              </>
            )}
          </Button>
        </motion.div>
      </motion.form>

      <motion.div
        className="text-center space-y-4"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 0.8 }}
      >
        <div className="flex items-center justify-center space-x-2 text-purple-600">
          <Heart className="w-4 h-4" />
          <span className="text-sm font-medium">I tuoi dati sono protetti</span>
        </div>
        
        <p className="text-xs text-gray-500 max-w-xs mx-auto leading-relaxed">
          Tutti i tuoi dati vengono salvati solo localmente sul tuo dispositivo. 
          La registrazione serve solo per identificarti in modo sicuro.
        </p>
      </motion.div>
    </div>
  )
}